class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;
    
    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.refreshAndOpen(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.refreshAndOpen(cartLink);
      }
    });
  }

  refreshAndOpen(triggeredBy) {
    // Використовуємо той самий механізм оновлення, що і в updateQuantity()
    const cartDrawerItems = this.querySelector('cart-drawer-items');
    if (!cartDrawerItems) {
      this.open(triggeredBy);
      return;
    }

    // Отримуємо список секцій для оновлення (як в updateQuantity)
    const sectionsToRender = cartDrawerItems.getSectionsToRender();
    const sectionIds = sectionsToRender.map(section => section.section);

    // Отримуємо актуальний стан кошика та секції паралельно
    Promise.all([
      fetch(`${routes.cart_url}.js`).then(r => r.json()),
      ...sectionIds.map(sectionId => 
        fetch(`${routes.cart_url}?section_id=${sectionId}`).then(r => r.text()).catch(err => {
          console.error(`Error fetching section ${sectionId}:`, err);
          throw err;
        })
      )
    ])
      .then(([cartData, ...sectionHtmls]) => {
        // Оновлюємо стан кошика (як в updateQuantity)
        this.classList.toggle('is-empty', cartData.item_count === 0);
        const cartFooter = document.getElementById('main-cart-footer');
        if (cartFooter) {
          cartFooter.classList.toggle('is-empty', cartData.item_count === 0);
        }

        // Оновлюємо секції так само, як в updateQuantity (рядки 285-292)
        const sectionsMap = {};
        sectionIds.forEach((sectionId, index) => {
          sectionsMap[sectionId] = sectionHtmls[index];
        });

        sectionsToRender.forEach((section) => {
          const sectionHtml = sectionsMap[section.section];
          if (!sectionHtml) {
            console.warn(`No HTML for section:`, section.section);
            return;
          }

          const elementToReplace =
            document.getElementById(section.id)?.querySelector(section.selector) || 
            document.getElementById(section.id);
          
          if (elementToReplace) {
            try {
              const innerHTML = cartDrawerItems.getSectionInnerHTML(
                sectionHtml,
                section.selector
              );
              elementToReplace.innerHTML = innerHTML;
            } catch (err) {
              console.error(`Error updating section ${section.id}:`, err);
            }
          } else {
            console.warn(`Element not found for section:`, section.id);
          }
        });

        // Оновлюємо стан компонентів (як в updateQuantity)
        if (cartDrawerItems) {
          cartDrawerItems.updateCartUpsellToggleState();
          cartDrawerItems.updateCartUpsellVisibility(cartData.item_count);
        }

        // Публікуємо подію оновлення кошика (як в updateQuantity)
        if (typeof publish === 'function' && window.PUB_SUB_EVENTS?.cartUpdate) {
          publish(PUB_SUB_EVENTS.cartUpdate, { 
            source: 'cart-drawer-open', 
            cartData: cartData 
          });
        }
      })
      .then(() => {
        // Відкриваємо drawer після оновлення
        this.open(triggeredBy);
      })
      .catch((e) => {
        console.error('Error refreshing cart drawer:', e);
        // Навіть при помилці відкриваємо — UX краще
        this.open(triggeredBy);
      });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
          const cartDrawerItems = this.querySelector('cart-drawer-items');
    if (cartDrawerItems) {
      cartDrawerItems.updateCartUpsellToggleState();
      cartDrawerItems.updateCartUpsellVisibility();
    }

    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }

  // Update cart drawer sections from addToCart response
  // Accepts sections object from Shopify sections API response
  // Format: { 'cart-drawer': '<html>', 'cart-icon-bubble': '<html>' }
  updateSections(sections) {
    if (!sections) return;

    // Update cart-drawer section
    if (sections['cart-drawer']) {
      const html = new DOMParser().parseFromString(sections['cart-drawer'], 'text/html');
      
      // Update cart-drawer-items and footer using replaceWith (like onCartUpdate does)
      const selectors = ['cart-drawer-items', '.cart-drawer__footer'];
      for (const selector of selectors) {
        const targetElement = document.querySelector(selector);
        const sourceElement = html.querySelector(selector);
        if (targetElement && sourceElement) {
          targetElement.replaceWith(sourceElement);
        }
      }

      // Update is-empty class based on cart state
      const parsedStateElement = html.querySelector('[data-cart-drawer-state]');
      if (parsedStateElement) {
        try {
          const parsedState = JSON.parse(parsedStateElement.textContent);
          this.classList.toggle('is-empty', parsedState.item_count === 0);
          
          // Update cart drawer items component state
          const cartDrawerItemsElement = this.querySelector('cart-drawer-items');
          if (cartDrawerItemsElement) {
            cartDrawerItemsElement.updateCartUpsellToggleState();
            cartDrawerItemsElement.updateCartUpsellVisibility(parsedState.item_count);
          }
        } catch (e) {
          console.warn('Failed to parse cart drawer state:', e);
        }
      }
    }

    // Update cart-icon-bubble section
    if (sections['cart-icon-bubble']) {
      const cartIconSection = document.getElementById('cart-icon-bubble');
      if (cartIconSection) {
        const html = new DOMParser().parseFromString(sections['cart-icon-bubble'], 'text/html');
        const sourceElement = html.querySelector('.shopify-section');
        if (sourceElement) {
          cartIconSection.innerHTML = sourceElement.innerHTML;
        }
      }
    }
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);